import { ReactNode, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import styles from './ExpandableText.module.scss';
import { formatTextContent } from '../../utils/textFormatUtils';
import { caretRightPurple } from '../../assets/hostedassets';

interface ExpandableTextProps {
    content: string;
    // Fired the first time the user expands (e.g. for analytics).
    onExpand?: () => void;
    // Extra content revealed below the text when expanded; the toggle stays beneath it.
    expandedContent?: ReactNode;
}

// Clamp height when collapsed (~5 lines). Keep in sync with $clamp-height in the stylesheet.
const CLAMP_HEIGHT = '7.5rem';

function ExpandableText({ content, onExpand, expandedContent }: ExpandableTextProps) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [fullHeight, setFullHeight] = useState(0);
    const contentRef = useRef<HTMLDivElement>(null);

    // While collapsed, measure the full content height so we can animate to/from the real size.
    useEffect(() => {
        if (expanded) return;
        const el = contentRef.current;
        if (!el) return;
        const measure = () => {
            setFullHeight(el.scrollHeight);
            setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [content, expanded]);

    const handleToggle = () => {
        if (!expanded) onExpand?.();
        setExpanded(!expanded);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.contentWrap}>
                <div ref={contentRef} className={styles.content} style={{ maxHeight: expanded ? `${fullHeight}px` : CLAMP_HEIGHT }}>
                    {formatTextContent(content)}
                </div>
                {!expanded && isOverflowing && <div className={styles.fade} aria-hidden />}
            </div>
            {expanded && expandedContent}
            {isOverflowing && (
                <span className={styles.toggle} onClick={handleToggle}>
                    {expanded ? 'See less' : 'See more'}
                    <img src={caretRightPurple} alt="" className={clsx(styles.chevron, { [styles.chevronExpanded]: expanded })} />
                </span>
            )}
        </div>
    );
}

export default ExpandableText;
